import React from 'react';
import styles from './lab-value-info.module.scss';
import ReactMarkdown from 'react-markdown';
import { Lang } from 'shared/constants';
import { Modal } from 'src/components/design-system';
import { LabBreadcrumbs } from 'src/components/breadcrumbs/LabBreadcrumbs';
import { TagsIcons } from 'src/components/tags/tagsIcons';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS } from 'shared/constants';
import { TextTag } from 'src/components/tags/text-tags';
import { Owners } from 'src/components/common/owners';

const DetailInfo = ({ label, value, link }) => {
  if (!value) return false;

  return (
    <div className={styles.detail}>
      <h6>{label}</h6>
      <div className={styles.contentDetails}>
        {!link && value}
        {link && (
          <a href={link} target="_blank" rel="noreferrer">
            {value}
          </a>
        )}
      </div>
    </div>
  );
};

const MrkdwnDetailInfo = ({ label, value }) => {
  if (!value) return false;

  return (
    <div className={styles.detail}>
      <h6>{label}</h6>
      <div className={styles.contentDetails}>
        <ReactMarkdown children={value} />
      </div>
    </div>
  );
};

const LabValueInfoComponent = React.memo(function LabValueInfoComponent({
  id = null,
  item = {},
  lang = Lang.LAB_INFORMATION_MODAL,
  labSetItemDetails = () => {},
  parents = [],
  labSelectValues = () => {},
}) {
  const close = React.useCallback(
    ({ id = '', title = '', tags = {}, ownerIds = '' } = {}) => {
      if (id.length > 0) {
        analyticsService.track(EVENTS.LAB_ITEMS.CLOSE_INFORMATION, {
          id: id.toString(),
          title: title,
          ownerIds,
          ...tags,
        });
      }

      labSetItemDetails(null);
    },
    [labSetItemDetails],
  );

  const onNavigateToCategory = id => {
    close();
    labSelectValues(id);
  };

  let { reference, info, url, tags } = item ?? {};

  const renderTags = () => {
    if (!tags?.contentType && !tags?.specialty) return null;

    return (
      <div className={styles.tagsContainer}>
        <TagsIcons tags={tags} hideTags={{ targetAudience: true, contentType: true }} />
        <TextTag tag={tags?.contentType} />
      </div>
    );
  };

  return (
    <Modal open={!!id} title={lang.HEADER} onCancel={() => close(item)} cancelText={lang.DISMISS}>
      <div className={styles.tagsAndOwnersWrapper}>
        <Owners
          ownerIds={item.ownerIds}
          title={lang.VALIDATED_BY}
          titleClassName={styles.ownerTitle}
          onNavigate={close}
        />
        {renderTags()}
      </div>

      <div className={styles.data}>
        <MrkdwnDetailInfo label={lang.INFO} value={info} />

        <DetailInfo
          label={lang.REFERENCE}
          value={reference && lang.REFERENCE_LINK_PLACEHOLDER}
          link={reference}
        />

        <DetailInfo label={lang.URL} value={url && lang.URL_CALCULATOR} link={url} />
      </div>

      <div className={styles.detail}>
        <h6>{lang.PARENTS_PATH}</h6>
        {parents.map(category => (
          <LabBreadcrumbs
            wrapperStyle={styles.breadcrumbs}
            pathParts={category.pathParts}
            title={category.title}
            key={category.id}
            onCurrentClick={() => onNavigateToCategory(category.id)}
            hideRoot
            initialWorkspaceId={category.workspaceId}
            onPathClick={close}
          />
        ))}
      </div>
    </Modal>
  );
});

export { LabValueInfoComponent };
